import {useDispatch, useSelector} from "react-redux";
import useToast from "../../../util/useToast";
import {selectAllAccounts} from "../../../slices/accountsSlice";
import {
  editorConfigSelector,
  rxHideTransactionEditor,
  rxShowHideDeleteConfirmationModal,
  rxUpdateTransactions
} from "../../../slices/transactionsSlice";
import {useCallback, useEffect, useState} from "react";
import {AMOUNT_REGEX, MODE_EDIT, TRANSACTION_TYPE_DEFAULT, TRANSACTION_TYPE_TRANSFER} from "../../../codes";
import moment from "moment";
import {createTransaction, deleteTransaction, updateTransaction} from "../../../api/ApiTransactions";
import {useTranslation} from "react-i18next";
import {selectAllRules} from "../../../slices/rulesSlice";
import {findRule, getOrDefault} from "../../../util/util";
import {selectAllCategories} from "../../../slices/categoriesSlice";
import {appConfigSelector} from "../../../slices/appSlice";
import useNetwork from "../../../util/useNetwork";
import useValidator from "../../../util/useValidator";

export const TYPE_DEBIT = 1;
export const TYPE_CREDIT = 2;
export const TYPE_TRANSFER = 3;

const useTransactionEditor = () => {
  const dispatch = useDispatch();

  const {t} = useTranslation("transactions");
  const {showToastSuccess} = useToast();
  const {handleNetworkError} = useNetwork();
  const validate = useValidator();

  const accounts = useSelector(selectAllAccounts);
  const transactionRules = useSelector(selectAllRules);
  const transactionCategories = useSelector(selectAllCategories);
  const {defaultTransactionsAccount} = useSelector(appConfigSelector);

  const {show, mode, transaction, deleteConfirmationModal} = useSelector(editorConfigSelector);

  const [editedTransaction, setEditedTransaction] = useState({errors: {}, amount: "", payee: "", note: "", tags: ""});
  const [availableAccounts, setAvailableAccounts] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);

  const hideTransactionEditor = () => dispatch(rxHideTransactionEditor());

  useEffect(() => {
    if (!transaction || !show) {
      return;
    }

    const values = {
      errors: {},
      amount: transaction.amount === undefined ? "" : "" + Math.abs(transaction.amount),
      category: getOrDefault(transaction.category, null),
      periodicity: getOrDefault(transaction.periodicity, null),
      valueDate: getOrDefault(transaction.valueDate, moment().format("YYYY-MM-DD")),
      note: getOrDefault(transaction.note, ""),
      tags: getOrDefault(transaction.tags, []).join(" ")
    };

    if (transaction.type === TRANSACTION_TYPE_TRANSFER) {
      values.type = TYPE_TRANSFER;
      values.account = transaction.amount < 0 ? transaction.account : transaction.payee;
      values.payee = transaction.amount < 0 ? transaction.payee : transaction.account;
    } else {
      values.type = transaction.amount === undefined || transaction.amount < 0 ? TYPE_DEBIT : TYPE_CREDIT;
      values.account = transaction.account === undefined ? defaultTransactionsAccount : transaction.account;
      values.payee = transaction.payee || "";

      if (!values.account) {
        values.account = accounts[0].id;
      }

      const rule = findRule(transactionRules, values.payee);

      if (rule) {
        values.rule = rule;

        if (rule.payee !== undefined) {
          values.payeeDisplay = rule.payee;
        }
      }
    }

    setEditedTransaction(values);

    setAvailableAccounts(() => {
      if (values.type === TYPE_TRANSFER) {
        return accounts.filter(a => a.active || a.id === values.account || a.id === defaultTransactionsAccount || a.id === values.payee);
      } else {
        return accounts.filter(a => a.active || a.id === values.account || a.id === defaultTransactionsAccount);
      }
    });

    setAvailableCategories(() => transactionCategories.filter(c => c.active || c.id === values.category));
  }, [show, transaction, accounts, defaultTransactionsAccount, transactionRules, transactionCategories]);

  const getErrors = values => {
    const errors = {};

    if (values.amount === undefined) {
      errors.amount = "transaction_editor_error_amount_must_be_filled";
    }

    if (!values.payee.trim().length) {
      errors.payee = "transaction_editor_error_payee_must_be_filled";
    }

    return errors;
  };

  const submit = e => {
    e.preventDefault();

    let type, amount;

    if (editedTransaction.amount.length) {
      amount = +editedTransaction.amount.replace(",", ".");
    }

    if (editedTransaction.type === TYPE_TRANSFER) {
      type = TRANSACTION_TYPE_TRANSFER;
    } else {
      type = TRANSACTION_TYPE_DEFAULT;
      if (editedTransaction.type === TYPE_DEBIT && amount !== undefined) {
        amount = -amount;
      }
    }

    const values = {
      updateAccountBalance: true,
      type,
      amount,
      account: editedTransaction.account,
      payee: editedTransaction.payee,
      payeeDisplay: editedTransaction.payeeDisplay,
      valueDate: editedTransaction.valueDate,
      today: moment().format("YYYY-MM-DD"),
      category: editedTransaction.category,
      periodicity: editedTransaction.periodicity,
      note: editedTransaction.note,
      tags: editedTransaction.tags.split(" ").map(tag => tag.trim()).filter(tag => tag.length > 0)
    };

    return validate(getErrors(values))
      .then(() => {
        if (mode === MODE_EDIT) {
          return updateTransaction(transaction.id, values);
        } else {
          return createTransaction(values);
        }
      })
      .then(result => dispatch(rxUpdateTransactions(result)))
      .then(() => {
        showToastSuccess(t(mode === MODE_EDIT ? "transaction_edited" : "transaction_added"));

        hideTransactionEditor();
      })
      .catch(error => {
        handleNetworkError(error, {
          400: err => setEditedTransaction(prev => ({...prev, errors: err.response.data}))
        });
      });
  };

  const updateEditedTransaction = field => event => {
    setEditedTransaction(prev => {
      const values = {};

      switch (field) {
        case "type":
          values.type = event;

          if (values.type === TYPE_TRANSFER) {
            values.payee = availableAccounts.filter(a => a.id !== editedTransaction.account)[0].id;
            values.category = null;
          } else if (editedTransaction.type === TYPE_TRANSFER) {
            values.payee = "";
          }

          break;
        case "account":
          values.account = event;

          if (editedTransaction.type === TYPE_TRANSFER && prev.payee === values.account) {
            values.payee = availableAccounts.filter(a => a.id !== values.account)[0].id;
          }

          break;
        case "other_account":
          values.payee = event;
          break;
        case "amount": {
          const amount = event.target.value;

          if (AMOUNT_REGEX.test(amount)) {
            values.amount = amount;
          }

          break;
        }
        case "valueDate":
          values.valueDate = event;
          break;
        case "category":
          values.category = event;
          break;
        case "periodicity":
          values.periodicity = event;
          break;
        case "payee": {
          values.payee = event.target.value;
          values.rule = null;
          values.payeeDisplay = null;

          const rule = findRule(transactionRules, values.payee);

          if (rule) {
            values.rule = rule;
            values.payeeDisplay = rule.payee;
            values.category = rule.category || editedTransaction.category;
            values.tags = rule.tags ? rule.tags.join(" ") : editedTransaction.tags;
          }

          break;
        }
        default:
          values[field] = event.target.value;
          break;
      }

      return ({...prev, ...values});
    });
  };

  const showDeleteConfirmationModal = useCallback((() => dispatch(rxShowHideDeleteConfirmationModal(true))), [dispatch]);
  const hideDeleteConfirmationModal = useCallback((() => dispatch(rxShowHideDeleteConfirmationModal(false))), [dispatch]);

  const onDeleteTransaction = () => {
    return deleteTransaction(transaction)
      .then(result => {
        dispatch(rxUpdateTransactions(result));
        dispatch(rxHideTransactionEditor());
      })
      .catch(handleNetworkError)
      .finally(hideDeleteConfirmationModal);
  };

  return {
    show,
    t,
    mode,
    submit,
    hideTransactionEditor,
    transaction,
    editedTransaction,
    updateEditedTransaction,
    availableAccounts,
    availableCategories,
    deleteConfirmationModal,
    showDeleteConfirmationModal,
    hideDeleteConfirmationModal,
    onDeleteTransaction
  };
};

export default useTransactionEditor;
